import { resolveAuthStorageMode, resolveSsoCookieNames } from "./auth-config";

// Token storage for the add-in. Keys match the legacy Triyo.Addin.Web add-in
// for compatibility. Remembered sessions use localStorage; session-only logins
// use sessionStorage so closing Outlook clears the token (the authStorage
// config key can force one container instead).
const TOKEN_KEY = "tmpTriyoToken";
const JWT_KEY = "tmpJwtTriyoToken";
const GRAPH_KEY = "tmpGraphToken";
const AUTH_KEYS = [TOKEN_KEY, JWT_KEY, GRAPH_KEY] as const;

/**
 * Default cookie names set by Triyo.Web after a successful browser login
 * (legacy SSO). Deployments can override the base name via the
 * AUTH_COOKIE_NAME config key — reads go through resolveSsoCookieNames().
 */
export const SSO_COOKIE_TOKEN = "triyo-token-app";
export const SSO_COOKIE_TOKEN_API = "triyo-token-app-api";

function getStoredValue(key: string): string | null {
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key);
}

export function getToken(): string | null {
  return getStoredValue(TOKEN_KEY);
}

export function getJwtToken(): string | null {
  return getStoredValue(JWT_KEY);
}

export function getGraphToken(): string | null {
  return getStoredValue(GRAPH_KEY);
}

export function setAuthToken(token: string, jwtToken?: string, graphToken?: string, remember = true): void {
  removeAuthToken();
  const mode = resolveAuthStorageMode();
  const useLocalStorage = mode === "localStorage" || (mode === "auto" && remember);
  const storage = useLocalStorage ? window.localStorage : window.sessionStorage;
  storage.setItem(TOKEN_KEY, token);
  if (jwtToken !== undefined) {
    storage.setItem(JWT_KEY, jwtToken);
  }
  if (graphToken !== undefined) {
    storage.setItem(GRAPH_KEY, graphToken);
  }
}

export function removeAuthToken(): void {
  AUTH_KEYS.forEach((key) => {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  });
}

/** Reads a single document cookie by name (legacy SSO cookie flow). */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Returns the TRIYO web SSO cookie token if present.
 * Prefers the configured base cookie name (default `triyo-token-app`), then its
 * `-api` variant (legacy Login.js order).
 */
export function getSsoCookieToken(): string | null {
  const [primaryName, fallbackName] = resolveSsoCookieNames();
  return getCookie(primaryName) || getCookie(fallbackName);
}

// Minimal JWT payload decoder (avoids the jsonwebtoken dependency).
export function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
  } catch {
    return {};
  }
}
